import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@risksmart-app/atomic-ui';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { FC } from 'react';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import ConfirmModal from 'src/components/confirm-modal/ConfirmModal';

import { useArchiveUser, useRestoreUser } from '@/hooks/mutations/user';
import { useIsCustomerSupport } from '@/hooks/useIsCustomerSupport';

type UserStatusValue = 'active' | 'archived' | null | undefined;

export interface UserStatusCellProps {
  userId: string;
  /** The org-scoped status to display (falls back to account-level if absent). */
  status: UserStatusValue;
  /** Display name shown in the confirmation dialog. */
  userName: string;
  /** External_Id on the user row — non-null means SCIM-provisioned. */
  userExternalId: string | null | undefined;
  /** External_Id on the active organisationuser row. */
  orgUserExternalId: string | null | undefined;
}

/**
 * Renders the Status column for a user in Settings → Users as a small
 * button. The button is enabled only when the caller is a CustomerSupport
 * user acting on a non-SCIM, non-self user — in which case clicking it
 * opens a confirmation dialog directly (no intermediate dropdown), since
 * each row has only one valid transition. SCIM users get a popover
 * explaining that offboarding must go through their IdP.
 */
const UserStatusCell: FC<UserStatusCellProps> = ({
  userId,
  status,
  userName,
  userExternalId,
  orgUserExternalId,
}) => {
  const { t } = useTranslation(['common'], {
    keyPrefix: 'userSettings.archive',
  });
  const isCallerCS = useIsCustomerSupport();
  const { user: currentUser } = useRisksmartUser();
  const { addNotification } = useNotifications();
  const { archiveUser, loading: archiving } = useArchiveUser();
  const { restoreUser, loading: restoring } = useRestoreUser();

  const [confirmOpen, setConfirmOpen] = useState(false);

  const isScimManaged = !!(userExternalId || orgUserExternalId);
  const isSelf = currentUser?.userId === userId;
  const displayStatus = status ?? 'active';
  const statusLabel = String(displayStatus);
  const isLoading = archiving || restoring;

  const isInteractive =
    isCallerCS &&
    !isScimManaged &&
    !isSelf &&
    (displayStatus === 'active' || displayStatus === 'archived');

  const nextAction: 'archive' | 'restore' =
    displayStatus === 'active' ? 'archive' : 'restore';
  const confirmTitle =
    nextAction === 'archive'
      ? t('confirm_archive_title')
      : t('confirm_restore_title');
  const confirmBodyKey =
    nextAction === 'archive'
      ? 'userSettings.archive.confirm_archive_body'
      : 'userSettings.archive.confirm_restore_body';
  const successMessage =
    nextAction === 'archive' ? t('archive_success') : t('restore_success');

  const onConfirm = async () => {
    try {
      if (nextAction === 'archive') {
        await archiveUser(userId);
      } else {
        await restoreUser(userId);
      }
      addNotification({ type: 'success', content: successMessage });
      setConfirmOpen(false);
    } catch {
      addNotification({ type: 'error', content: t('action_failed') });
      setConfirmOpen(false);
    }
  };

  const statusButton = (
    <Button
      size={'sm'}
      radius={'full'}
      disabled={!isInteractive}
      onClick={() => setConfirmOpen(true)}
    >
      {statusLabel}
    </Button>
  );

  return (
    <>
      {isScimManaged && isCallerCS ? (
        <Popover>
          <PopoverTrigger render={statusButton} />
          <PopoverContent>{t('scim_tooltip')}</PopoverContent>
        </Popover>
      ) : (
        statusButton
      )}
      <ConfirmModal
        isVisible={confirmOpen}
        onConfirm={() => void onConfirm()}
        onDismiss={() => setConfirmOpen(false)}
        header={confirmTitle}
        loading={isLoading}
      >
        <Trans i18nKey={confirmBodyKey} values={{ name: userName }} />
      </ConfirmModal>
    </>
  );
};

export default UserStatusCell;
